import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import type { JSX } from 'react'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { Device } from '@/pages/Device/Device'
import {
  labelNoErrorColor,
  labelRoot,
  radioGroupLabelSx,
  requiredIndicator,
  selectStandardSx,
} from '@/utils/theme'
import { DeviceRole, DeviceRoleBoxLabel } from '@/pages/calibration/calibration'

const DATE_FORMAT = 'yyyy-MM-dd'

export function showFieldError(fieldState: {
  error?: { type: string; message?: string }
}): string | undefined {
  if (!fieldState.error) return undefined
  // Suppress 'required' errors - asterisk on label handles that visually
  // Show all other errors - wrong value, custom validation, etc.
  if (fieldState.error.type === 'required') return undefined
  return fieldState.error.message
}
export interface SharedPreFlightFieldsProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  control: Control<TFieldValues>
  isSubmitted: boolean
  hasBoard: boolean
  minInstalledDate: Date
  countryItems: { id: string; label: string }[]
  deviceRoles: DeviceRole[]
  deviceRolesLoading: boolean
  hasConfigurableServiceInterval: boolean
  isSaving: boolean
  onCountryChange: (country: string) => void
  onYearsIntervalChange: (years: number) => void
  device: Device
  setDevice?: (device: Device) => void
}

const SharedPreFlightFields = <TFieldValues extends FieldValues>({
  control,
  isSubmitted,
  hasBoard,
  minInstalledDate,
  countryItems,
  deviceRoles,
  deviceRolesLoading,
  hasConfigurableServiceInterval,
  isSaving,
  onCountryChange,
  onYearsIntervalChange,
  device,
  setDevice,
}: SharedPreFlightFieldsProps<TFieldValues>): JSX.Element => {
  const { t } = useTranslation(['app', 'common', 'di'])

  return (
    <>
      {/* installedDate - only for devices with battery board */}
      {hasBoard && (
        <Controller
          name="installedDate"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="date"
              label={t('app:device.calibration.preFlightStep.installedDate')}
              fullWidth
              variant="standard"
              slotProps={{
                inputLabel: {
                  shrink: true,
                  sx: {
                    ...labelRoot,
                    ...requiredIndicator,
                    ...labelNoErrorColor,
                    position: 'relative',
                    transform: 'none',
                  },
                },
                htmlInput: {
                  min: format(minInstalledDate, DATE_FORMAT),
                  max: format(new Date(), DATE_FORMAT),
                  autoComplete: 'off',
                },
              }}
              error={isSubmitted && Boolean(fieldState.error)}
              helperText={isSubmitted ? showFieldError(fieldState) : undefined}
              sx={{ mb: 3 }}
            />
          )}
        />
      )}

      {/* Country */}
      <Controller
        name="country"
        control={control}
        render={({ field, fieldState }) => (
          <FormControl
            fullWidth
            variant="standard"
            sx={{ mb: 2.5, width: '100%' }}
            error={isSubmitted && Boolean(fieldState.error)}
          >
            <InputLabel
              sx={{
                ...labelRoot,
                ...requiredIndicator,
                ...labelNoErrorColor,
                position: 'relative',
                transform: 'none',
              }}
            >
              {t('di:device.country')}
            </InputLabel>
            <Select
              {...field}
              variant="standard"
              sx={selectStandardSx}
              onChange={(e) => {
                field.onChange(e)
                const newCountry = e.target.value
                onCountryChange(newCountry)
                if (setDevice) setDevice({ ...device, country: newCountry })
              }}
            >
              {countryItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            {isSubmitted && showFieldError(fieldState) && (
              <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      {/* Device role = enabled only for RSS, disabled for all others */}
      <Controller
        name="deviceRoleCode"
        control={control}
        render={({ field, fieldState }) => (
          <FormControl
            fullWidth
            variant="standard"
            sx={{ mb: 2.5, width: '100%' }}
            error={isSubmitted && Boolean(fieldState.error)}
          >
            <InputLabel
              sx={{
                ...labelRoot,
                ...requiredIndicator,
                ...labelNoErrorColor,
                position: 'relative',
                transform: 'none',
              }}
            >
              {t('app:device.calibration.preFlightStep.deviceRole')}
            </InputLabel>
            <Select
              {...field}
              variant="standard"
              sx={selectStandardSx}
              disabled={deviceRoles.length <= 1 || deviceRolesLoading}
            >
              {deviceRoles.map((role) => (
                <MenuItem key={role.deviceRoleCode} value={role.deviceRoleCode}>
                  {DeviceRoleBoxLabel(role)}
                </MenuItem>
              ))}
            </Select>
            {isSubmitted && showFieldError(fieldState) && (
              <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      {/* Years between maintenance = only for devices with configurable interval */}

      {hasConfigurableServiceInterval && (
        <Controller
          name="yearsBetweenMaintenance"
          control={control}
          render={({ field, fieldState }) => {
            const hasError = isSubmitted && Boolean(fieldState.error)
            return (
              <FormControl
                component="fieldset"
                error={hasError}
                sx={{ mb: 2.5, width: '100%', display: 'block' }}
              >
                <Typography
                  sx={{
                    ...radioGroupLabelSx,
                    ...requiredIndicator,
                    position: 'relative',
                    transform: 'none',
                  }}
                >
                  {t(
                    'app:device.calibration.preFlightStep.yearsBetweenMaintenance',
                  )}
                </Typography>
                <RadioGroup
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    onYearsIntervalChange(Number.parseInt(e.target.value, 10))
                  }}
                >
                  {['2', '4'].map((val) => (
                    <FormControlLabel
                      key={val}
                      value={val}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: hasError
                              ? 'error.main'
                              : 'rgba(0, 0, 0, 0.54)',
                            '&.Mui-checked': {
                              color: hasError
                                ? 'error.main'
                                : 'rgba(0, 0, 0, 0.87)',
                            },
                          }}
                        />
                      }
                      label={val}
                      disabled={isSaving}
                    />
                  ))}
                </RadioGroup>
                {isSubmitted && showFieldError(fieldState) && (
                  <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
                )}
              </FormControl>
            )
          }}
        />
      )}
    </>
  )
}

export default SharedPreFlightFields
